import {
  UPDATE_BOOKING,
  UPDATE_BOOKINGS,
  CLEAR_BOOKING,
  CLEAR_RESERVATION,
  UPDATE_ORIGINAL_BOOKING,
  SET_LAST_REFRESH_TIME,
  SET_REFRESH_POPUP_DISMISS_TIME,
  UPDATE_ASSISTANCE_COPY_STATUS,
} from '@mutations'
import { BookingsService } from '@common/ApiService'
import bookingParams from '@utils/booking-params'
import { updateField } from '@state/helpers'
import { deepClone } from 'fast-json-patch'
import { updateJourneyLeg } from '@state/builders/journey-legs/helpers'
import paginationHeaders from '@/src/utils/pagination-headers'

const UPDATE_PAGINATION = 'UPDATE_PAGINATION'
const UPDATE_PAGE = 'UPDATE_PAGE'
const BOOKINGS_PROGRESS = 'BOOKING_PROGRESS'
const BOOKINGS_ERROR = 'BOOKINGS_ERROR'

const defaultBookingState = () => {
  return {
    booking: {
      source: 'phone',
      journeys: [],
      customer_ref: null,
      rars_ref: null,
      ref: null,
    },
    bookings: [],
    pagination: {},
    progress: '',
    originalBooking: null,
    lastRefreshTime: null,
    assistanceCopyStatus: false,
    refreshPopupDismissTime: null,
  }
}

export const state = defaultBookingState()

export const getters = {}

export const mutations = {
  updateField,

  [UPDATE_BOOKING](state, data) {
    state.booking = JSON.parse(JSON.stringify(data))
    return state
  },

  [UPDATE_ASSISTANCE_COPY_STATUS](state, data) {
    state.assistanceCopyStatus = data
    return state
  },

  [UPDATE_BOOKINGS](state, data) {
    state.bookings = data
    return state
  },

  [UPDATE_ORIGINAL_BOOKING](state, data) {
    state.originalBooking = data
    return state
  },

  [UPDATE_PAGINATION](state, response) {
    state.pagination = paginationHeaders(response)
    return state
  },

  [UPDATE_PAGE](state, page) {
    state.pagination = { ...state.pagination, page }
    return state.pagination
  },

  [BOOKINGS_PROGRESS](state, progress) {
    state.progress = progress
    return state.progress
  },

  [CLEAR_BOOKING](state) {
    Object.assign(state, defaultBookingState())
  },

  [BOOKINGS_ERROR](state, data) {
    state.errors = data
    state.progress = 'error'
    return state
  },

  [CLEAR_RESERVATION](state) {
    state.booking.rars_ref = null
    updateJourneyLeg({
      where: (leg) => {
        return (
          leg.reservations.length > 0 && leg.reservation_source !== 'manual'
        )
      },
      data: { seats: [], coach: '', reservations: [] },
    })
  },

  [SET_LAST_REFRESH_TIME](state) {
    state.lastRefreshTime = new Date()
  },

  [SET_REFRESH_POPUP_DISMISS_TIME](state) {
    state.refreshPopupDismissTime = new Date()
  },
}

export const actions = {
  createV2({ commit }, booking) {
    return new Promise((resolve, reject) => {
      BookingsService.create(bookingParams(booking), 'v2')
        .then((response) => {
          if (response.isAxiosError) {
            reject(response)
          } else {
            commit(UPDATE_BOOKING, response.data)
            resolve(response.data)
          }
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  createRecurringBookingV2({ commit }, booking) {
    return new Promise((resolve, reject) => {
      BookingsService.create(bookingParams(booking, { recurring: true }), 'v2')
        .then((response) => {
          if (response.isAxiosError) {
            reject(response)
          } else {
            resolve(response.data)
          }
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  updateBookingV2({ commit }, booking) {
    return new Promise((resolve, reject) => {
      BookingsService.update(booking.ref, bookingParams(booking), 'v2')
        .then((response) => {
          commit(UPDATE_BOOKING, response.data)
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  getBooking({ commit }, id) {
    return new Promise((resolve, reject) => {
      BookingsService.get(id)
        .then((response) => {
          commit(UPDATE_BOOKING, response.data)
          commit(UPDATE_ORIGINAL_BOOKING, response.data)
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  getDuplicatedBooking({ commit }, payload) {
    commit('journeyPlanner/toggleLoading', null, { root: true })

    return new Promise((resolve, reject) => {
      BookingsService.getDuplicate(payload)
        .then((response) => {
          const booking = deepClone(response.data)
          commit(UPDATE_BOOKING, {
            customer_ref: booking?.customer.ref,
            journeys: booking.journeys,
            source: booking.source || [],
            ref: null,
            journey_planner_response: booking.journey_planner_response,
          })

          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
        .finally(() => {
          commit('journeyPlanner/toggleLoading', null, { root: true })
        })
    })
  },

  getDuplicatePossibility({ commit }, payload) {
    return new Promise((resolve, reject) => {
      BookingsService.getDuplicatePossibility(payload)
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  incompleteBooking({ commit }, params) {
    return new Promise((resolve, reject) => {
      BookingsService.incompleteBooking(params)
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  confirmBooking({ commit }, bookingRef) {
    return BookingsService.confirm(bookingRef).then((response) => {
      commit(UPDATE_BOOKING, response.data)
    })
  },

  rejectBooking({ commit }, params) {
    return BookingsService.reject(params.bookingRef, {
      rejection_reason: params.rejectionReason,
    }).then((response) => {
      commit(UPDATE_BOOKING, response.data)
    })
  },

  cancelBooking({ commit }, params) {
    return BookingsService.cancelBooking(params)
  },

  fetchUnconfirmedBookings({ commit }, params) {
    if (params.page) {
      commit(UPDATE_PAGE, params.page)
    }

    return new Promise((resolve) => {
      commit(BOOKINGS_PROGRESS, 'search_loading')
      return BookingsService.indexUnconfirmed(params)
        .then((response) => {
          commit(UPDATE_BOOKINGS, response.data)
          commit(UPDATE_PAGINATION, response)
          commit(BOOKINGS_PROGRESS, 'booking_success')
          commit(SET_LAST_REFRESH_TIME)
          commit(SET_REFRESH_POPUP_DISMISS_TIME)
          resolve(response.data)
        })
        .catch((error) => commit(BOOKINGS_ERROR, error))
    })
  },

  setBooking({ commit }, booking) {
    commit(UPDATE_BOOKING, booking)
  },

  setAssistanceCopyStatus({ commit }, status) {
    commit(UPDATE_ASSISTANCE_COPY_STATUS, status)
  },

  clearBooking({ commit }) {
    commit(CLEAR_BOOKING)
  },

  resendEmail({ commit }, params) {
    return BookingsService.resendEmail(params)
  },
}
